import React from 'react';
import { useLanguage } from '../components/LanguageContext';
import { motion } from 'framer-motion';
import { Lock, Eye, Database, Trash2, Share2, Cookie } from 'lucide-react';

export default function Privacy() {
  const { language, isRTL } = useLanguage();

  const sections = language === 'he' ? [
    {
      icon: Database,
      title: 'מידע שאנו אוספים',
      content: `אנו אוספים את המידע הבא:
• כתובת אימייל ושם מלא בעת ההרשמה
• תמונות שאתם מעלים לשירות
• נתוני שימוש ופעילות באתר
• מידע על המכשיר והדפדפן שלכם

המידע נאסף רק כדי לספק לכם את השירות ולשפר את חוויית המשתמש.`
    },
    {
      icon: Lock,
      title: 'אבטחת מידע',
      content: `אנו משתמשים באמצעי אבטחה מתקדמים להגנה על המידע שלכם:
• הצפנת SSL לכל התקשורת
• אחסון מאובטח בשרתים מוגנים
• גישה מוגבלת למידע רק לעובדים מורשים
• גיבויים יומיים ושחזור מידע`
    },
    {
      icon: Eye,
      title: 'שימוש במידע',
      content: `אנו משתמשים במידע שלכם למטרות הבאות:
• מתן השירות ועיבוד התמונות
• שליחת עדכונים ומידע חשוב
• שיפור השירות וחוויית המשתמש
• מניעת הונאות ושימוש לרעה

לא נמכור או נשתף את המידע שלכם עם צדדים שלישיים למטרות שיווקיות.`
    },
    {
      icon: Share2,
      title: 'שיתוף מידע',
      content: `נשתף מידע רק במקרים הבאים:
• עם ספקי שירות שעוזרים לנו להפעיל את האתר
• כאשר נדרש על פי חוק
• להגנה על זכויותינו או בטיחות המשתמשים
• בהסכמתכם המפורשת`
    },
    {
      icon: Trash2,
      title: 'מחיקת מידע',
      content: `יש לכם את הזכות לבקש:
• עותק של כל המידע שאנו מחזיקים עליכם
• תיקון מידע שגוי
• מחיקת המידע שלכם מהמערכות שלנו
• הגבלת העיבוד של המידע שלכם

לבקשות, פנו אלינו בעמוד יצירת קשר.`
    },
    {
      icon: Cookie,
      title: 'עוגיות (Cookies)',
      content: `אנו משתמשים בעוגיות לצורך:
• שמירת העדפות השפה והתצוגה שלכם
• ניתוח תעבורה ושימוש באתר
• שיפור חוויית המשתמש

תוכלו לנהל את העדפות העוגיות דרך הגדרות הדפדפן שלכם.`
    }
  ] : [
    {
      icon: Database,
      title: 'Information We Collect',
      content: `We collect the following information:
• Email address and full name during registration
• Images you upload to the service
• Usage data and activity on the website
• Device and browser information

Information is collected only to provide you with the service and improve user experience.`
    },
    {
      icon: Lock,
      title: 'Data Security',
      content: `We use advanced security measures to protect your information:
• SSL encryption for all communications
• Secure storage on protected servers
• Limited access to information only for authorized employees
• Daily backups and data recovery`
    },
    {
      icon: Eye,
      title: 'Use of Information',
      content: `We use your information for the following purposes:
• Providing the service and processing images
• Sending updates and important information
• Improving the service and user experience
• Preventing fraud and misuse

We will not sell or share your information with third parties for marketing purposes.`
    },
    {
      icon: Share2,
      title: 'Information Sharing',
      content: `We share information only in the following cases:
• With service providers who help us operate the website
• When required by law
• To protect our rights or user safety
• With your explicit consent`
    },
    {
      icon: Trash2,
      title: 'Data Deletion',
      content: `You have the right to request:
• A copy of all information we hold about you
• Correction of incorrect information
• Deletion of your information from our systems
• Restriction of processing your information

For requests, contact us on the contact page.`
    },
    {
      icon: Cookie,
      title: 'Cookies',
      content: `We use cookies for:
• Saving your language and display preferences
• Analyzing traffic and website usage
• Improving user experience

You can manage cookie preferences through your browser settings.`
    }
  ];

  return (
    <div className="min-h-screen py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-white mb-4">
            {language === 'he' ? 'מדיניות פרטיות' : 'Privacy Policy'}
          </h1>
          <p className="text-slate-400">
            {language === 'he' ? 'עדכון אחרון: ינואר 2024' : 'Last updated: January 2024'}
          </p>
        </motion.div>

        <div className="space-y-6">
          {sections.map((section, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="glass-card rounded-2xl p-8"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-pink-600/20 flex items-center justify-center">
                  <section.icon className="w-6 h-6 text-pink-400" />
                </div>
                <h2 className="text-xl font-semibold text-white">{section.title}</h2>
              </div>
              <div className="text-slate-400 leading-relaxed whitespace-pre-line">
                {section.content}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
